import { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ArrowRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const faqs = [
    {
      id: '1',
      question: 'How fast can I get approved for finance?',
      answer: 'At Flash Funding, we pride ourselves on speed. Many applications receive a decision within 24 hours, and straightforward applications with complete documentation can be approved same day. Timing depends on the applicant, the lender’s credit criteria and the type of loan. Our streamlined digital process means less paperwork and faster outcomes for your business.',
      category: 'General'
    },
    {
      id: '2',
      question: 'What types of equipment can I finance?',
      answer: 'We finance a wide range of commercial equipment including construction machinery (excavators, loaders, cranes), manufacturing equipment, medical equipment, hospitality equipment (commercial kitchens, refrigeration), IT infrastructure, agricultural machinery, and more. If it helps your business operate, we can likely help you finance it.',
      category: 'Equipment'
    },
    {
      id: '3',
      question: 'What vehicles do you finance?',
      answer: 'We offer finance for all commercial vehicles including prime movers, rigid trucks, utes, vans, trailers, forklifts, buses, and specialised vehicles. Both new and used vehicles are eligible, and we work with all major dealerships across Australia.',
      category: 'Vehicles'
    },
    {
      id: '4',
      question: 'What are your interest rates?',
      answer: 'Our rates are competitive and tailored to your specific situation. Factors that influence your rate include the asset type, loan term, your business history, credit profile, and the loan-to-value ratio. We work with a panel of over 40 lenders to find you the best deal. Contact us for a personalised quote — it only takes a few minutes.',
      category: 'General'
    },
    {
      id: '5',
      question: 'Do I need to provide a deposit?',
      answer: 'Deposit requirements vary based on the asset and your business profile. In many cases, we can offer 100% finance with no deposit required. For some equipment types or credit profiles, a deposit of 10-20% may result in better rates. We\'ll discuss all options during your application.',
      category: 'General'
    },
    {
      id: '6',
      question: 'How does a business line of credit work?',
      answer: 'A business line of credit provides flexible access to funds up to an approved limit. You only pay interest on the amount you\'ve drawn, not the full limit. As you repay, funds become available again — like a revolving door of capital. It\'s ideal for managing cash flow, covering unexpected expenses, or taking advantage of opportunities as they arise.',
      category: 'Business Loans'
    },
    {
      id: '7',
      question: 'Can I get finance if my business is new?',
      answer: 'Yes! While established businesses may have more options, we regularly help new businesses secure funding. We\'ll look at factors like your industry experience, business plan, personal credit history, and the asset being financed. Many of our lenders will finance businesses from day one of trading.',
      category: 'General'
    },
    {
      id: '8',
      question: 'What documents do I need to apply?',
      answer: 'For most applications, we need: valid driver\'s licence or passport, recent business bank statements (3-6 months), ABN/ACN details, and quotes for the asset you\'re financing. For larger loans (typically over $150k), we may also request financial statements (tax returns, BAS statements). Our team will guide you through exactly what\'s needed.',
      category: 'General'
    },
    {
      id: '9',
      question: 'What is chattel mortgage?',
      answer: 'A chattel mortgage is a popular financing option where the lender provides funds to purchase an asset, and you own the asset from day one. The lender holds a "mortgage" over the chattel (moveable property) until the loan is repaid. Benefits include potential tax advantages — you may be able to claim GST on purchase and depreciation. It\'s particularly popular for vehicles and equipment.',
      category: 'Equipment'
    },
    {
      id: '10',
      question: 'Can I pay off my loan early?',
      answer: 'Yes, in most cases you can make extra payments or pay off your loan early. Many of our products have no early repayment penalties. Some fixed-rate products may have break costs — we\'ll clearly explain any terms before you sign. Flexibility is important to us, and we structure deals with your interests in mind.',
      category: 'General'
    },
    {
      id: '11',
      question: 'Do you finance used equipment and vehicles?',
      answer: 'Absolutely. We finance both new and used assets. For used equipment, we\'ll typically consider the age, condition, and remaining useful life. Most lenders will finance equipment up to 10-15 years old, and vehicles up to 12-15 years old. Older assets may require larger deposits or shorter terms.',
      category: 'Equipment'
    },
    {
      id: '12',
      question: 'What is the application process?',
      answer: 'Our process is simple: 1) Contact us or fill out our online form with basic details. 2) We\'ll call to discuss your needs and gather information. 3) Submit your application with supporting documents. 4) Receive approval (often within 24 hours, depending on the applicant, the lender’s credit criteria and the type of loan). 5) Sign documents and settle — funds released to vendor. Most deals complete within 2-3 business days.',
      category: 'General'
    }
  ];

  const categories = ['all', 'General', 'Equipment', 'Vehicles', 'Business Loans'];

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="FAQ | Frequently Asked Questions About Commercial Finance"
        description="Find answers to common questions about commercial asset finance, equipment loans, vehicle finance, and business lending. Learn about approval times, rates, deposits, and more."
        keywords="commercial finance FAQ, equipment finance questions, business loan FAQ, vehicle finance questions, chattel mortgage explained, finance approval process"
        canonical="/faq"
      />
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-6">
              <HelpCircle size={16} className="text-gold-500" />
              <span className="text-gold-500 font-body text-sm font-medium">Got Questions?</span>
            </div>
            <h1 className="font-heading font-black text-5xl sm:text-6xl text-white uppercase tracking-tight leading-none mb-6">
              Frequently Asked<br />
              <span className="gold-gradient-text">Questions</span>
            </h1>
            <p className="font-body text-lg sm:text-xl text-white/70">
              Find answers to common questions about our commercial finance solutions.
              Can't find what you're looking for? Get in touch.
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-navy-800 border-y border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40" />
              <Input
                data-testid="faq-search-input"
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-navy-900 border-white/10 text-white h-12"
              />
            </div>
            {/* Categories */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              {categories.map((category) => (
                <button
                  key={category}
                  data-testid={`faq-category-${category}`}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 font-body text-sm whitespace-nowrap transition-colors
                    ${activeCategory === category 
                      ? 'bg-gold-500 text-black' 
                      : 'bg-navy-700 text-white/70 hover:bg-navy-600'
                    }`}
                >
                  {category === 'all' ? 'All' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 lg:py-24 bg-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFaqs.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <AccordionItem 
                  key={faq.id} 
                  value={faq.id}
                  className="bg-navy-800 border border-white/5 px-6 animate-fade-in"
                  style={{ animationDelay: `${0.05 * index}s` }}
                >
                  <AccordionTrigger 
                    data-testid={`faq-question-${faq.id}`}
                    className="text-left font-heading font-bold text-lg text-white hover:text-gold-500 py-6"
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-white/70 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-16">
              <HelpCircle size={48} className="text-white/20 mx-auto mb-4" />
              <p className="font-body text-white/60">No questions found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-20 lg:py-28 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight mb-6">
            Still Have Questions?
          </h2>
          <p className="font-body text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            Our team is here to help. Get in touch for personalised advice 
            about your commercial finance needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="faq-contact-btn"
                className="gold-gradient text-black font-heading font-bold text-lg uppercase tracking-wider px-8 py-6 hover:scale-105 transition-transform gold-glow"
              >
                Contact Us
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <a href="tel:+611800003527">
              <Button 
                variant="outline"
                className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
              >
                Call 1800 003 527
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
